import React, { useState, useEffect, useMemo } from 'react';
import { apiEndpoints } from '../services/api';
import SupplierForm from '../components/SupplierForm';
import EventForm from '../components/EventForm';
import DocumentForm from '../components/DocumentForm';
import Modal from '../components/Modal';
import { confirmDialog } from '../utils/confirm';
import { useAppContext } from '../contexts/AppContext';

const CATEGORY_COLORS = {
  serving:    { bg: '#d1fae5', text: '#059669' },
  tinware:    { bg: '#ede9fe', text: '#7c3aed' },
  wheels:     { bg: '#dbeafe', text: '#2563eb' },
  rare:       { bg: '#fef3c7', text: '#d97706' },
  electrical: { bg: '#fce7f3', text: '#db2777' },
  other:      { bg: '#f3f4f6', text: '#6b7280' },
};

const AVATAR_COLORS = [
  '#6366f1','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6',
];

function getCategoryStyle(cat) {
  return CATEGORY_COLORS[(cat || '').toLowerCase()] || CATEGORY_COLORS.other;
}
function getAvatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length === 1 ? p[0][0].toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function StarRating({ rating, size = 14 }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20" fill={i <= rating ? '#f59e0b' : '#e5e7eb'}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function Field({ label, value, name, onChange, type = 'text', multiline = false, half = false }) {
  const inputStyle = {
    padding: '9px 12px', borderRadius: 8, fontSize: 13,
    border: '1.5px solid #e5e7eb', background: '#f9fafb',
    color: '#111827', fontFamily: 'inherit',
    outline: 'none', resize: multiline ? 'vertical' : 'none',
    transition: 'border-color 0.15s',
  };
  return (
    <div className={half ? 'supplier-field-half' : 'supplier-field-full'}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: 0.3 }}>{label}</label>
      {multiline ? (
        <textarea
          name={name} value={value || ''} onChange={onChange}
          rows={3} style={inputStyle}
        />
      ) : (
        <input
          type={type} name={name} value={value || ''} onChange={onChange}
          style={inputStyle}
          onFocus={e => (e.target.style.borderColor = '#3b82f6')}
          onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
        />
      )}
    </div>
  );
}

function ActionBtn({ label, icon, color = '#0f2d52', onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '12px 16px',
        background: color, color: '#fff',
        border: 'none', borderRadius: 8,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
        cursor: 'pointer', textTransform: 'uppercase',
        textAlign: 'left', fontFamily: 'inherit',
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
    >
      <span style={{ flexShrink: 0 }}>{icon}</span>
      {label}
    </button>
  );
}

/* ── Supplier stats modal content ────────────────────────────────────────── */
function SupplierStatsContent({ supplier }) {
  const rows = [
    { label: 'Category',            value: supplier.category || '—' },
    { label: 'Rating',              value: supplier.rating ? `${supplier.rating} / 5` : '—' },
    { label: 'Phone',               value: supplier.phone || '—' },
    { label: 'Email',               value: supplier.email || '—' },
    { label: 'Address',             value: [supplier.address, supplier.city].filter(Boolean).join(', ') || '—' },
    { label: 'Working Hours',       value: supplier.hours || '—' },
    { label: 'Car Types Serviced',  value: supplier.carTypes || '—' },
    { label: 'Services',            value: supplier.services || '—' },
    { label: 'Appointment Method',  value: supplier.appointment || '—' },
    { label: 'Contact Person',      value: supplier.contactPerson || '—' },
    { label: 'Waiting Time',        value: supplier.waitingTime || '—' },
    { label: 'Immobilization Time', value: supplier.immobilizationTime || '—' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {rows.map(({ label, value }) => (
        <div key={label} className="info-row">
          <div className="info-lbl">{label}</div>
          <div className="info-val">{value}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Detail view ─────────────────────────────────────────────────────────── */
function SupplierDetail({ supplier: initial, onBack, onRefresh, isAdmin }) {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [supplierEvents, setSupplierEvents] = useState([]);

  useEffect(() => { setForm({ ...initial }); }, [initial]);

  const fetchSupplierEvents = async () => {
    try {
      const res = await apiEndpoints.events.list();
      const all = res.data || [];
      // Older supplier events (before the dedicated supplierName field existed)
      // were stored with the supplier's name stuffed into `plate` — keep
      // matching those too so existing history doesn't disappear.
      setSupplierEvents(all.filter(e => e.supplierName === initial.name || (!e.supplierName && e.plate === initial.name)));
    } catch { /* silent */ }
  };

  useEffect(() => { fetchSupplierEvents(); }, [initial.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiEndpoints.suppliers.update(initial._id, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onRefresh();
    } catch {
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!await confirmDialog(`Delete "${initial.name}"? This cannot be undone.`)) return;
    try {
      await apiEndpoints.suppliers.delete(initial._id);
      onRefresh();
      onBack();
    } catch {
      alert('Failed to delete supplier.');
    }
  };

  const handleGenerateEmail = () => {
    if (!form.email) {
      alert('No email address set for this supplier.\nPlease add an email in the Edit details section and save first.');
      return;
    }
    const subject = encodeURIComponent(`Partnership — ${initial.name}`);
    const body = encodeURIComponent(
      `Dear ${form.contactName || initial.name},\n\nI am contacting you regarding our partnership.\n\nPlease let us know if you have any questions.\n\nBest regards`
    );
    window.open(`mailto:${form.email}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleExportData = () => {
    const headers = [
      'Name','Category','Rating','Phone','Emergency Phone','Email',
      'Contact Name','Emergency Contact','Address','City','Hours',
      'Car Types','Services','Appointment','Contact Person',
      'Waiting Time','Immobilization Time','Observations',
    ];
    const row = [
      form.name, form.category, form.rating, form.phone, form.phone2, form.email,
      form.contactName, form.emergencyContact, form.address, form.city, form.hours,
      form.carTypes, form.services, form.appointment, form.contactPerson,
      form.waitingTime, form.immobilizationTime, form.observations,
    ];
    const csv = [headers, row]
      .map((r) => r.map((v) => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `supplier_${(form.name || 'export').replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Let EventForm/DocumentForm handle their own error alerts — just throw on failure
  const handleSaveEvent = async (payload) => {
    await apiEndpoints.events.create(payload);
    await fetchSupplierEvents();
  };

  const handleSaveDocument = async (documentData, file) => {
    await apiEndpoints.documents.create(documentData, file);
  };

  const avatarBg = getAvatarColor(initial.name);
  const catStyle = getCategoryStyle(form.category);
  const fullAddress = [form.address, form.city].filter(Boolean).join(', ');

  return (
    <div>
      {/* Back link */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#3b82f6', fontSize: 13, fontWeight: 600,
          padding: '0 0 16px 0', fontFamily: 'inherit',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd"/>
        </svg>
        Back to suppliers
      </button>

      {/* Supplier header card */}
      <div className="supplier-header-info">
        <div style={{
          width: 56, height: 56, borderRadius: 16, flexShrink: 0,
          background: avatarBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 20,
        }}>
          {getInitials(initial.name)}
        </div>

        <div className="supplier-header-meta">
          {form.carTypes && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280', fontSize: 13 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 17H3v-5l3.5-6H18l3 6v5h-2"/><circle cx="7.5" cy="17" r="2.5"/><circle cx="16.5" cy="17" r="2.5"/></svg>
              {form.carTypes}
            </span>
          )}
          {form.hours && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280', fontSize: 13 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {form.hours}
            </span>
          )}
          {fullAddress && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280', fontSize: 13 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {fullAddress}
            </span>
          )}
        </div>

        <div className="supplier-header-summary">
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{initial.name}</div>
          {form.category && (
            <span style={{
              background: catStyle.bg, color: catStyle.text,
              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginTop: 4, display: 'inline-block',
            }}>{form.category}</span>
          )}
          <div style={{ marginTop: 4 }}><StarRating rating={form.rating || 0} size={13} /></div>
          {form.phone && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{form.phone}</div>}
        </div>
      </div>

      {/* Main two-column layout */}
      <div className="supplier-detail-grid">

        {/* Left: Edit details */}
        <div className="supplier-detail-form">
          <div className="card">
            <div className="card-body" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Edit details</span>
                {isAdmin && (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      background: saved ? '#10b981' : '#1d4ed8',
                      color: '#fff', border: 'none', borderRadius: 8,
                      padding: '8px 20px', fontSize: 13, fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                      transition: 'background 0.2s',
                    }}
                  >
                    {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
                  </button>
                )}
              </div>

              <div className="supplier-fields-grid">
                <Field label="Company Name" name="name" value={form.name} onChange={handleChange} half />
                <Field label="Category" name="category" value={form.category} onChange={handleChange} half />
                <Field label="Phone Contact" name="phone" value={form.phone} onChange={handleChange} half />
                <Field label="Emergency Phone" name="phone2" value={form.phone2} onChange={handleChange} half />
                <Field label="Email" name="email" value={form.email} onChange={handleChange} type="email" half />
                <Field label="Name Contact" name="contactName" value={form.contactName} onChange={handleChange} half />
                <Field label="Emergency Contact" name="emergencyContact" value={form.emergencyContact} onChange={handleChange} half />
                <Field label="Address" name="address" value={form.address} onChange={handleChange} half />
                <Field label="City" name="city" value={form.city} onChange={handleChange} half />
                <Field label="Program" name="hours" value={form.hours} onChange={handleChange} />
                <Field label="Car Type" name="carTypes" value={form.carTypes} onChange={handleChange} />
                <Field label="Services" name="services" value={form.services} onChange={handleChange} multiline />
                <Field label="Appointment" name="appointment" value={form.appointment} onChange={handleChange} half />
                <Field label="Contact person" name="contactPerson" value={form.contactPerson} onChange={handleChange} half />
                <Field label="Waiting time" name="waitingTime" value={form.waitingTime} onChange={handleChange} half />
                <Field label="Immobilization time" name="immobilizationTime" value={form.immobilizationTime} onChange={handleChange} half />
                <Field label="Observations" name="observations" value={form.observations} onChange={handleChange} multiline />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="supplier-detail-actions">
          <div className="card">
            <div className="card-body" style={{ padding: '20px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 14 }}>Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <ActionBtn label="Add Event" onClick={() => setShowEventForm(true)} icon={
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/></svg>
                } />
                <ActionBtn label="Generate Contact Email" onClick={handleGenerateEmail} icon={
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                } />
                {isAdmin && (
                  <ActionBtn label="Add Documents" onClick={() => setShowDocumentForm(true)} icon={
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/></svg>
                  } />
                )}
                <ActionBtn label="See Statistics" onClick={() => setShowStatsModal(true)} icon={
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>
                } />
                <ActionBtn label="Export Data" onClick={handleExportData} icon={
                  <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                } />
                {isAdmin && (
                  <ActionBtn label="Delete Partner" color="#dc2626" onClick={handleDelete} icon={
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                  } />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active events */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-body" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Active events</span>
            <button className="btn btn-primary btn-sm" onClick={() => setShowEventForm(true)}>+ Event</button>
          </div>
          <div className="vehicle-table-card">
            <table className="vehicle-table">
              <thead>
                <tr>
                  <th>EVENT</th>
                  <th>CREATION DATE</th>
                  <th>EST. COMPLETION</th>
                  <th>AMOUNT</th>
                  <th>STATUS</th>
                  <th>RESPONSIBLE</th>
                </tr>
              </thead>
              <tbody>
                {supplierEvents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-row">No active events linked to this partner.</td>
                  </tr>
                ) : (
                  supplierEvents.map((ev) => (
                    <tr key={ev._id}>
                      <td className="cell-title">{ev.evt || '—'}</td>
                      <td data-label="Creation Date">{ev.created ? ev.created.slice(0, 10) : '—'}</td>
                      <td data-label="Est. Completion">{ev.estEnd ? ev.estEnd.slice(0, 10) : '—'}</td>
                      <td data-label="Amount">{ev.amount != null ? `${ev.amount.toLocaleString()} ${ev.currency || 'RON'}` : '—'}</td>
                      <td data-label="Status">
                        <span style={{
                          background: ev.status === 'Finalizat' ? '#d1fae5' : '#dbeafe',
                          color: ev.status === 'Finalizat' ? '#059669' : '#2563eb',
                          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                        }}>{ev.status || 'Activ'}</span>
                      </td>
                      <td data-label="Responsible">{ev.resp || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Event modal */}
      <EventForm
        isOpen={showEventForm}
        onClose={() => setShowEventForm(false)}
        supplierName={initial.name}
        onSave={handleSaveEvent}
      />

      {/* Add Documents modal */}
      <DocumentForm
        isOpen={showDocumentForm}
        onClose={() => setShowDocumentForm(false)}
        supplierName={initial.name}
        onSave={handleSaveDocument}
      />

      {/* Statistics modal */}
      <Modal isOpen={showStatsModal} onClose={() => setShowStatsModal(false)} title={`${initial.name} — Statistics`} size="md">
        <SupplierStatsContent supplier={form} />
      </Modal>
    </div>
  );
}

/* ── List view ───────────────────────────────────────────────────────────── */
function CategoryPill({ label, count, active, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 12px', borderRadius: 20,
      border: active ? '2px solid #1d4ed8' : '1.5px solid #e5e7eb',
      background: active ? '#1d4ed8' : '#fff',
      color: active ? '#fff' : (color || '#374151'),
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 5,
    }}>
      {label}
      {count > 0 && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
          color: active ? '#fff' : '#6b7280',
          borderRadius: 10, fontSize: 11, padding: '1px 6px', fontWeight: 700,
        }}>{count}</span>
      )}
    </button>
  );
}

function MetaRow({ icon, children }) {
  const icons = {
    pin:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    phone: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.6 19.78 19.78 0 01.04 1.01 2 2 0 012.02 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>,
    clock: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 12 }}>
      <span style={{ opacity: 0.7 }}>{icons[icon]}</span>
      <span>{children}</span>
    </div>
  );
}

function SupplierCard({ supplier, onSelect }) {
  const catStyle = getCategoryStyle(supplier.category);
  const avatarBg = getAvatarColor(supplier.name);
  const address = [supplier.address, supplier.city].filter(Boolean).join(', ');

  return (
    <div className="card" onClick={onSelect} style={{ cursor: 'pointer' }}>
      <div className="card-body" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{
            width: 50, height: 50, borderRadius: 14, flexShrink: 0,
            background: avatarBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 18,
          }}>
            {getInitials(supplier.name)}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{supplier.name}</span>
              {supplier.category && (
                <span style={{ background: catStyle.bg, color: catStyle.text, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>
                  {supplier.category}
                </span>
              )}
            </div>
            {supplier.rating > 0 && <div style={{ marginBottom: 8 }}><StarRating rating={supplier.rating} /></div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {address && <MetaRow icon="pin">{address}</MetaRow>}
              {supplier.phone && <MetaRow icon="phone">{supplier.phone}</MetaRow>}
              {supplier.hours && <MetaRow icon="clock">{supplier.hours}</MetaRow>}
            </div>
          </div>

          <svg width="16" height="16" viewBox="0 0 20 20" fill="#9ca3af" style={{ flexShrink: 0, marginTop: 4 }}>
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
const Suppliers = () => {
  const { isAdmin } = useAppContext();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.suppliers.list();
      setSuppliers(res.data || []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  // Keep selectedSupplier in sync after a save
  useEffect(() => {
    if (selectedSupplier) {
      const updated = suppliers.find(s => s._id === selectedSupplier._id);
      if (updated) setSelectedSupplier(updated);
    }
  }, [suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    const seen = new Set();
    suppliers.forEach(s => { if (s.category) seen.add(s.category.toLowerCase()); });
    return Array.from(seen).sort();
  }, [suppliers]);

  const filtered = useMemo(() => {
    let list = suppliers;
    if (activeCategory !== 'all') list = list.filter(s => (s.category || '').toLowerCase() === activeCategory);
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(s => [s.name, s.category, s.city, s.phone, s.address].filter(Boolean).join(' ').toLowerCase().includes(q));
    }
    return list;
  }, [suppliers, activeCategory, searchTerm]);

  const handleSaveNew = async (data) => {
    await apiEndpoints.suppliers.create(data);
    await fetchSuppliers();
  };

  if (loading) return <div className="page-loading">Loading suppliers…</div>;

  if (selectedSupplier) {
    return (
      <SupplierDetail
        supplier={selectedSupplier}
        onBack={() => setSelectedSupplier(null)}
        onRefresh={fetchSuppliers}
        isAdmin={isAdmin}
      />
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Suppliers / Partners</div>
          <div className="ph-sub">{suppliers.length} active partner{suppliers.length !== 1 ? 's' : ''}</div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ marginRight: 6 }}>
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
            </svg>
            Add
          </button>
        )}
      </div>

      <div className="table-filters">
        <div className="filter-row">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ opacity: 0.4 }}>
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
            </svg>
            <input
              type="search" placeholder="Search supplier, city, phone…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="supplier-category-pills">
            <CategoryPill label="All" count={suppliers.length} active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} />
            {categories.map(cat => (
              <CategoryPill
                key={cat} label={cat}
                count={suppliers.filter(s => (s.category || '').toLowerCase() === cat).length}
                active={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
                color={getCategoryStyle(cat).text}
              />
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af' }}>
            {suppliers.length === 0 ? 'No suppliers yet. Add your first partner.' : 'No suppliers match the filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(s => (
            <SupplierCard key={s._id} supplier={s} onSelect={() => setSelectedSupplier(s)} />
          ))}
        </div>
      )}

      <SupplierForm isOpen={showForm} onClose={() => setShowForm(false)} supplier={null} onSave={handleSaveNew} />
    </div>
  );
};

export default Suppliers;
